import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { EventImageApiResponse } from 'domain/event';
import { EventSeries } from 'domain/event/EventSeries';
import {
  mapEventSeriesDetailsFromApi,
  mapEventSeriesToUpdateEventSeriesApiPayload,
  mapEventSeriesWizardFormValuesToApi,
} from 'event/mappers';
import { axiosBaseQuery, getApiBaseUrl } from 'modules/api/utils';
import { generateFormData } from 'store/utils';

import { omitUndefined } from './utils';

import type {
  ConvertSingleEventToSeriesPayload,
  CreateEventPayload,
  CreateEventSeriesResponse,
  DeleteEventsFromSeriesArgs,
  UpdateEventPayload,
  UpdateEventSeriesImagePayload,
  UpdateEventSeriesQuestionsPayload,
} from './types';

// See eventApi's saveWithRetryOn502: the tag-category write rides on the series save, so a briefly
// unavailable tag service surfaces as 502. Retrying re-sends the same Idempotency-Key, so the backend
// resumes at the tag write instead of creating a second series.
const saveWithRetryOn502 = retry(axiosBaseQuery({ baseURL: getApiBaseUrl('v2') }), {
  // maxRetries and retryCondition are mutually exclusive in RTK Query's RetryOptions union, so the
  // attempt cap lives inside the condition: 3 retries, 502 only.
  retryCondition: (error, _args, { attempt }) =>
    attempt <= 3 && (error as { status?: number } | undefined)?.status === 502,
});

export const eventSeriesApi = createApi({
  reducerPath: 'eventSeriesApi',
  baseQuery: axiosBaseQuery({ baseURL: getApiBaseUrl('v1') }),
  tagTypes: ['EVENT_SERIES'],
  endpoints: (builder) => ({
    getEventSeriesDetails: builder.query<EventSeries, { seriesId: number }>({
      keepUnusedDataFor: 0,
      query: ({ seriesId }) => ({
        url: `/eventSeries/${seriesId}`,
      }),
      transformResponse: mapEventSeriesDetailsFromApi,
      providesTags: (_result, _error, { seriesId }) => [{ type: 'EVENT_SERIES', id: seriesId }],
    }),
    createEventSeries: builder.mutation<CreateEventSeriesResponse, CreateEventPayload>({
      queryFn: async ({ idempotencyKey, ...data }, api, extraOptions) => {
        const result = await saveWithRetryOn502(
          {
            url: '/eventSeries',
            method: 'POST',
            headers: { 'Idempotency-Key': idempotencyKey },
            data: mapEventSeriesWizardFormValuesToApi(data),
          },
          api,
          extraOptions
        );

        return result.error ? { error: result.error } : { data: result.data as CreateEventSeriesResponse };
      },
    }),
    updateEventSeries: builder.mutation<void, UpdateEventPayload>({
      queryFn: async ({ seriesId, idempotencyKey, ...data }, api, extraOptions) => {
        const result = await saveWithRetryOn502(
          {
            url: `/eventSeries/${seriesId}`,
            method: 'PUT',
            headers: { 'Idempotency-Key': idempotencyKey },
            data: mapEventSeriesWizardFormValuesToApi(data),
          },
          api,
          extraOptions
        );

        return result.error ? { error: result.error } : { data: undefined };
      },
      invalidatesTags: (_result, _error, { seriesId }) => [{ type: 'EVENT_SERIES', id: seriesId }],
    }),
    convertSingleEventToSeries: builder.mutation<CreateEventSeriesResponse, ConvertSingleEventToSeriesPayload>({
      queryFn: async ({ id, idempotencyKey, ...data }, api, extraOptions) => {
        const result = await saveWithRetryOn502(
          {
            url: `/eventSeries/withEvent/${id}`,
            method: 'POST',
            headers: { 'Idempotency-Key': idempotencyKey },
            data: mapEventSeriesWizardFormValuesToApi(data),
          },
          api,
          extraOptions
        );

        return result.error ? { error: result.error } : { data: result.data as CreateEventSeriesResponse };
      },
      invalidatesTags: (result) => (result ? [{ type: 'EVENT_SERIES', id: result.id }] : []),
    }),
    deleteEventSeries: builder.mutation<void, { seriesId: number }>({
      query: ({ seriesId }) => ({
        url: `/eventSeries/${seriesId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { seriesId }) => [{ type: 'EVENT_SERIES', id: seriesId }],
    }),
    // Deliberately still on v1. Removing occurrences carries no tag data, and the v2 PUT requires a
    // tagCategories array -- sending an empty one would clear the series' categories as a side effect of
    // removing an occurrence. The v1 PUT is not deprecated for exactly this reason.
    deleteEventsFromSeries: builder.mutation<void, DeleteEventsFromSeriesArgs>({
      query: ({ eventSeries, eventIdsToRemove }) => {
        if (eventSeries.events.length === eventIdsToRemove.length) {
          return {
            url: `/eventSeries/${eventSeries.id}`,
            method: 'DELETE',
          };
        }
        return {
          url: `/eventSeries/${eventSeries.id}`,
          method: 'PUT',
          data: mapEventSeriesToUpdateEventSeriesApiPayload({ data: eventSeries, eventIdsToRemove }),
        };
      },
      invalidatesTags: (_result, _error, { eventSeries }) => [{ type: 'EVENT_SERIES', id: eventSeries.id }],
    }),
    updateEventSeriesQuestions: builder.mutation<void, UpdateEventSeriesQuestionsPayload>({
      query: ({ seriesId, questions }) => ({
        url: `/eventSeries/${seriesId}/questions`,
        method: 'POST',
        data: questions,
      }),
      invalidatesTags: (_result, _error, { seriesId }) => [{ type: 'EVENT_SERIES', id: seriesId }],
    }),
    postEventSeriesImage: builder.mutation<EventImageApiResponse, UpdateEventSeriesImagePayload>({
      query: ({ seriesId, ...params }) => ({
        url: `/eventSeries/${seriesId}/images`,
        headers: { 'Content-Type': 'multipart/form-data' },
        method: 'POST',
        data: generateFormData(omitUndefined(params)),
      }),
      invalidatesTags: (_result, _error, { seriesId }) => [{ type: 'EVENT_SERIES', id: seriesId }],
    }),
  }),
});

export const {
  useCreateEventSeriesMutation,
  useGetEventSeriesDetailsQuery,
  useDeleteEventsFromSeriesMutation,
  useUpdateEventSeriesQuestionsMutation,
} = eventSeriesApi;
